import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  SnapshotMeta, SnapshotPayload, TrendPoint, Rec, Finding, Country, SyncStatus,
} from "@shared/types.js";
import { COUNTRIES } from "@shared/types.js";
import { computePic, coreScope, computePace, buildWorklist } from "@shared/stats.js";

import { api } from "./api.js";
import { TopNav, Hero, type TabKey } from "./components/Shell.js";
import { FilterBar } from "./components/FilterBar.js";
import { UploadModal } from "./components/UploadModal.js";
import { ManageModal } from "./components/ManageModal.js";
import { RecordDrawer } from "./components/RecordDrawer.js";
import { applyFilters, type FilterState } from "./lib/filters.js";
import { readUrl, writeUrl } from "./lib/urlState.js";
import { readCountry, saveCountry } from "./lib/country.js";
import { applyTheme, readTheme, watchSystem, NEXT_THEME, type Theme } from "./lib/theme.js";
import { signOutEverywhere } from "./lib/auth.js";
import {
  recordExport, findingExport, picExport, trendExport, worklistExport,
  downloadCsv, copyForExcel,
} from "./lib/exportData.js";
import { fnum } from "./lib/format.js";

import { Overview } from "./panels/Overview.js";
import { Worklist } from "./panels/Worklist.js";
import { RatePanel } from "./panels/RatePanel.js";
import { Components } from "./panels/Components.js";
import { DataTable } from "./panels/DataTable.js";
import { Crosscheck } from "./panels/Crosscheck.js";
import { Trend } from "./panels/Trend.js";
import { Pic } from "./panels/Pic.js";
import { ExportPanel } from "./panels/ExportPanel.js";
import { Mapping } from "./panels/Mapping.js";

const initial = readUrl();

/* Stable empty arrays: a fresh [] literal each render would make every
   downstream useMemo recompute and spin the effects below. */
const NO_ROWS: Rec[] = [];
const NO_FINDINGS: Finding[] = [];

export default function App() {
  /* Which Masterfile is open. Changing it reloads every dataset below, so it
     is deliberately the first piece of state the effects depend on. */
  const [country, setCountry] = useState(() => readCountry());
  const [snapshots, setSnapshots] = useState<SnapshotMeta[]>([]);
  const [snapId, setSnapId] = useState<number | null>(initial.snapId);
  const [snap, setSnap] = useState<SnapshotPayload | null>(null);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [targetDate, setTargetDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState("");

  const [tab, setTab] = useState<TabKey>(initial.tab);
  const [filters, setFilters] = useState<FilterState>(initial.filters);
  const [sevFilter, setSevFilter] = useState(initial.sev);
  const [ruleFilter, setRuleFilter] = useState(initial.rule);

  const [theme, setTheme] = useState<Theme>(() => readTheme());
  const [showUpload, setShowUpload] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [openRec, setOpenRec] = useState<Rec | null>(null);
  const [toast, setToast] = useState("");
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const toastTimer = useRef<number | undefined>(undefined);

  const say = useCallback((msg: string) => {
    setToast(msg);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 2600);
  }, []);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== "system") return;
    return watchSystem(() => applyTheme("system"));
  }, [theme]);

  /* ---- keep the URL in step with the view, so links are shareable ---- */
  const skipUrlWrite = useRef(false);
  const lastTab = useRef<TabKey>(tab);
  useEffect(() => {
    if (skipUrlWrite.current) {
      skipUrlWrite.current = false;
      lastTab.current = tab;
      return;
    }
    const next = writeUrl({ tab, snapId, filters, sev: sevFilter, rule: ruleFilter });
    if (next === window.location.search
      || next === window.location.pathname + window.location.search) return;

    /* Switching tab is a navigation worth a Back step; tweaking a filter or
       typing in the search box is not, or Back would replay every keystroke. */
    if (tab !== lastTab.current) window.history.pushState(null, "", next);
    else window.history.replaceState(null, "", next);
    lastTab.current = tab;
  }, [tab, snapId, filters, sevFilter, ruleFilter]);

  /* Back/forward should move between views, not leave the app. */
  useEffect(() => {
    const onPop = () => {
      const u = readUrl();
      skipUrlWrite.current = true;
      setTab(u.tab);
      setFilters(u.filters);
      setSevFilter(u.sev);
      setRuleFilter(u.rule);
      if (u.snapId !== null) setSnapId(u.snapId);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /* ---- data ---- */
  const loadList = useCallback(async () => {
    const list = await api.listSnapshots(country);
    setSnapshots(list);
    return list;
  }, [country]);

  /** Switching country invalidates the open snapshot: ids do not cross over. */
  const changeCountry = useCallback((next: Country) => {
    setCountry((cur) => {
      if (cur === next) return cur;
      saveCountry(next);
      setSnapId(null);
      setSnap(null);
      setTrend([]);
      setFatal("");
      setLoading(true);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [list, settings] = await Promise.all([loadList(), api.getSettings(country)]);
        if (cancelled) return;
        setTargetDate(settings.targetDate);
        if (list.length === 0) {
          const name = COUNTRIES.find((c) => c.key === country)?.label ?? country;
          setFatal(`No snapshots stored for ${name} yet. Upload its Masterfile, or sync from SharePoint.`);
          setLoading(false);
          return;
        }
        setSnapId((cur) => (cur !== null && list.some((s) => s.id === cur) ? cur : list[0]!.id));
      } catch (e) {
        if (!cancelled) {
          setFatal(e instanceof Error ? e.message : String(e));
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loadList, country]);

  useEffect(() => {
    if (snapId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const payload = await api.getSnapshot(snapId, country);
        if (cancelled) return;
        setSnap(payload);
        setFatal("");
      } catch (e) {
        if (!cancelled) setFatal(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [snapId, country]);

  useEffect(() => {
    void (async () => {
      try {
        setTrend(await api.getTrend(filters.includeCatE, country));
      } catch { /* trend is non-critical */ }
    })();
  }, [filters.includeCatE, snapshots, country]);

  /* Sync status is per country and cheap, so it just follows the switch. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await api.getSyncStatus(country);
        if (!cancelled) setSync(s);
      } catch { /* status is advisory; the dashboard works without it */ }
    })();
    return () => { cancelled = true; };
  }, [country, snapshots]);

  const allRows = snap?.records ?? NO_ROWS;
  const rows = useMemo(() => applyFilters(allRows, filters), [allRows, filters]);

  const pic = useMemo(() => computePic(coreScope(rows, true)), [rows]);
  const pace = useMemo(() => computePace(rows, targetDate), [rows, targetDate]);
  const worklist = useMemo(() => buildWorklist(rows), [rows]);

  /* Completion of the snapshot before the one on screen, for the delta chip. */
  const prevCompletion = useMemo(() => {
    const i = trend.findIndex((t) => t.snapshotId === snapId);
    return i > 0 ? trend[i - 1]!.stats.completion : null;
  }, [trend, snapId]);

  const scopedFindings = useMemo(() => {
    if (!snap) return NO_FINDINGS;
    const ids = new Set(rows.map((r) => r.uid));
    return snap.findings.filter((f) => ids.has(f.uid));
  }, [snap, rows]);

  const shownFindings = useMemo(
    () => scopedFindings.filter((f) =>
      (!sevFilter || f.severity === sevFilter) && (!ruleFilter || f.rule === ruleFilter)),
    [scopedFindings, sevFilter, ruleFilter],
  );

  /* ---- actions ---- */
  const runExport = useCallback(
    async (what: "records" | "findings" | "pic" | "trend" | "worklist", kind: "csv" | "copy") => {
      const sheet = what === "records" ? recordExport(rows)
        : what === "findings" ? findingExport(shownFindings)
        : what === "pic" ? picExport(pic)
        : what === "worklist" ? worklistExport(worklist)
        : trendExport(trend);
      if (kind === "csv") {
        downloadCsv(sheet);
        say(`Downloaded ${fnum(sheet.rows.length)} rows`);
      } else {
        const n = await copyForExcel(sheet);
        say(`Copied ${fnum(n)} rows — paste into Excel`);
      }
    },
    [rows, shownFindings, pic, trend, worklist, say],
  );

  const saveTarget = useCallback(async (d: string | null) => {
    try {
      const res = await api.setSettings({ targetDate: d }, country);
      setTargetDate(res.targetDate);
      say(d ? "Target date saved" : "Target date cleared");
    } catch (e) {
      say(e instanceof Error ? e.message : String(e));
    }
  }, [say, country]);

  const afterUpload = useCallback(async (id: number, notes: string[]) => {
    setShowUpload(false);
    await loadList();
    setSnapId(id);
    say(notes.length
      ? `Snapshot stored with ${notes.length} parser note${notes.length === 1 ? "" : "s"}`
      : "Masterfile parsed and stored");
  }, [loadList, say]);

  const afterManage = useCallback(async () => {
    const list = await loadList();
    if (list.length === 0) {
      setSnap(null);
      setSnapId(null);
      setFatal("No snapshots stored. Upload a Masterfile to begin.");
    } else if (!list.some((s) => s.id === snapId)) {
      setSnapId(list[0]!.id);
    }
    setTrend(await api.getTrend(filters.includeCatE, country));
  }, [loadList, snapId, filters.includeCatE, country]);

  /** Manual pull. The poller does this on a timer too; this is the "now" path. */
  const runSync = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await api.syncNow(country);
      setSync(res.status);
      if (res.changed) {
        const list = await loadList();
        if (res.snapshotId) setSnapId(res.snapshotId);
        else if (list.length > 0) setSnapId(list[0]!.id);
        say("Synced from SharePoint");
      } else {
        say(res.reason ?? "Already up to date");
      }
    } catch (e) {
      say(e instanceof Error ? e.message : String(e));
      try { setSync(await api.getSyncStatus(country)); } catch { /* keep the old status */ }
    } finally {
      setSyncing(false);
    }
  }, [country, loadList, say]);

  const counts: Partial<Record<TabKey, number>> = {
    worklist: worklist.length,
    data: rows.length,
    crosscheck: scopedFindings.length,
    trend: trend.length,
  };

  const current = snapshots.find((s) => s.id === snapId);

  /* Dropping the token and reloading is the whole of signing out: the gate in
     main.tsx reads the token on mount, finds none, and puts the login page
     back. Nothing else in the app holds a copy to clear. */
  /* Both doors, not just the one this person came through: clearing the
     password token while a Microsoft session cookie still opens the gate
     would look like sign-out did nothing. */
  const signOut = () => { void signOutEverywhere().then(() => window.location.reload()); };

  const topnav = (
    <TopNav
      tab={tab} onTab={setTab} counts={counts}
      search={filters.search}
      onSearch={(v) => setFilters((f) => ({ ...f, search: v }))}
      theme={theme} onCycleTheme={() => setTheme((t) => NEXT_THEME[t])}
      sync={sync} syncing={syncing} onSync={() => void runSync()}
      onOpenUpload={() => setShowUpload(true)} onManage={() => setShowManage(true)}
      onSignOut={signOut}
    />
  );

  const hero = (
    <Hero
      tab={tab} country={country} onCountry={changeCountry}
      snapshots={snapshots} currentId={snapId} onSelectSnapshot={setSnapId}
      recordCount={snap ? rows.length : null} snapshot={current}
    />
  );

  if (fatal && !snap) {
    return (
      <>
        {topnav}
        {hero}
        <main className="jx-card">
          <div className="card"><div className="empty">{fatal}</div></div>
        </main>
        {showUpload && (
          <UploadModal country={country} onClose={() => setShowUpload(false)} onDone={(id, n) => void afterUpload(id, n)} />
        )}
      </>
    );
  }

  return (
    <>
      {topnav}
      {hero}
      <main className="jx-card">
        {snap && <FilterBar all={allRows} rows={rows} filters={filters} onChange={setFilters} />}
        {loading && <div className="loadwrap"><span className="spinner" /> Loading snapshot…</div>}

        {!loading && snap && (
          <>
            {tab === "overview" && (
              <Overview
                rows={rows} findings={scopedFindings} pace={pace}
                targetDate={targetDate} prevCompletion={prevCompletion}
                onSetTarget={(d) => void saveTarget(d)}
                onOpenCrosscheck={() => setTab("crosscheck")}
                onOpenWorklist={() => setTab("worklist")}
                onPickStatus={(status) => setFilters((f) => ({ ...f, status }))}
              />
            )}
            {tab === "worklist" && (
              <Worklist items={worklist} onExport={(k) => void runExport("worklist", k)} />
            )}
            {tab === "newrate" && (
              <RatePanel mode="new" rows={rows} findings={snap.findings}
                onExport={(k) => void runExport("records", k)} />
            )}
            {tab === "estimation" && (
              <RatePanel mode="est" rows={rows} findings={snap.findings}
                onExport={(k) => void runExport("records", k)} />
            )}
            {tab === "components" && <Components rows={rows} allRows={allRows} />}
            {tab === "data" && (
              <DataTable
                rows={rows} onExport={(k) => void runExport("records", k)}
                onOpenRecord={setOpenRec}
              />
            )}
            {tab === "crosscheck" && (
              <Crosscheck
                rows={rows} findings={snap.findings} mirrors={snap.mirrors}
                sevFilter={sevFilter} ruleFilter={ruleFilter}
                onSevFilter={setSevFilter} onRuleFilter={setRuleFilter}
                onExport={(k) => void runExport("findings", k)}
              />
            )}
            {tab === "trend" && (
              <Trend points={trend} includeCatE={filters.includeCatE}
                onExport={(k) => void runExport("trend", k)} />
            )}
            {tab === "pic" && (
              <Pic
                pic={pic} rows={rows} activePic={filters.pic}
                onPicFilter={(p) => setFilters((f) => ({ ...f, pic: p }))}
                onExport={(k) => void runExport("pic", k)}
              />
            )}
            {tab === "export" && (
              <ExportPanel
                rows={rows} findings={shownFindings} pic={pic}
                trend={trend} worklist={worklist} onToast={say}
              />
            )}
            {tab === "mapping" && <Mapping snap={snap} />}
          </>
        )}
      </main>

      {openRec && (
        <RecordDrawer
          rec={openRec} findings={snap?.findings ?? NO_FINDINGS} onClose={() => setOpenRec(null)}
        />
      )}
      {showUpload && (
        <UploadModal country={country} onClose={() => setShowUpload(false)} onDone={(id, n) => void afterUpload(id, n)} />
      )}
      {showManage && (
        <ManageModal country={country}
          snapshots={snapshots} currentId={snapId}
          onClose={() => setShowManage(false)} onChanged={() => void afterManage()}
        />
      )}

      <div className={`toast${toast ? " on" : ""}`} role="status" aria-live="polite">{toast}</div>
    </>
  );
}
